import { useEffect, useState } from "react";
import { Footer } from "@/components/Footer";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ShieldAlert, ChevronLeft } from "lucide-react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { EmailLink } from "@/components/EmailLink";

const faqs = [
  {
    question: "What is the ClearMakes refund policy?",
    answer: "If a paid plan isn't right for you, ask within 14 days of a payment and we refund that payment in full. This covers your first payment, every renewal and yearly plans (the 14 days count from each yearly payment, including renewals), for every ClearMakes product. When we refund a payment, we also cancel that plan, and you move to Free."
  },
  {
    question: "How do I request a refund for my subscription?",
    answer: "Write to billing@clearmakes.com from the email on your account, or ask Paddle, our reseller, at paddle.net. The money usually reaches you in 5 to 10 business days."
  },
  {
    question: "Can I cancel my account after the 14-day period?",
    answer: "Yes, any time, from Settings in the app. Your plan stays until the end of the time you paid for, then you move to Free. Payments older than 14 days aren't refunded, and nothing is deleted: your workflows, apps and answers are kept."
  }
];

const Refund = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // FAQ Schema Markup (JSON-LD)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
          <HelmetProvider>
        <Helmet>
          <title>Refund Policy | ClearMakes</title>
          <meta name="description" content="ClearMakes Refund Policy. Understand our refund process for subscriptions. We strive to make every refund request straightforward and fair for our users." />
        </Helmet>
      </HelmetProvider>
<div className="min-h-screen bg-[#0A0F1C] text-foreground font-sans flex flex-col relative selection:bg-primary/30">
      {/* Inject JSON-LD Schema directly into the component */}
      
        
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "WebSite",
                  "@id": "https://clearmakes.com/#website",
                  "url": "https://clearmakes.com/",
                  "name": "ClearMakes",
                  "description": "The Professional Workspace for unified messaging and AI communication.",
                  "publisher": { "@id": "https://clearmakes.com/#organization" }
                },
                {
                  "@type": "Organization",
                  "@id": "https://clearmakes.com/#organization",
                  "name": "ClearMakes",
                  "url": "https://clearmakes.com/",
                  "logo": { "@type": "ImageObject", "url": "https://clearmakes.com/logo.png" },
                  "sameAs": [
                    "https://github.com/ClearMakes"
                  ]
                },
                {
                  "@type": "SoftwareApplication",
                  "@id": "https://clearmakes.com/#software",
                  "name": "ClearMakes Workspace",
                  "applicationCategory": "BusinessApplication",
                  "operatingSystem": "Web",
                  "url": "https://clearmakes.com/",
                  "offers": { "@type": "Offer", "price": "0.00", "priceCurrency": "USD" }
                }
              ]
            })
          }}
        />

      <div className="absolute inset-0 noise-texture opacity-20 pointer-events-none" />
      

      <main className="flex-grow pt-32 pb-20 container relative z-10 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6 border border-primary/20">
            <ShieldAlert className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white tracking-tight">Refund Policy</h1>
          <p className="text-lg text-muted-foreground">Cancellation and Refund Guidelines</p>
          <p className="text-sm text-gray-500 mt-2 font-mono uppercase tracking-widest">Last Updated: September 28, 2026</p>
        </div>

        <div className="space-y-10 text-gray-300 glass-panel border border-white/5 rounded-[2.5rem] p-8 md:p-16">
          <section className="bg-primary/5 p-8 rounded-2xl border border-primary/20 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
            <h2 className="text-2xl font-semibold text-white mb-4">14-Day Refund Guarantee</h2>
            <p className="mb-4 leading-relaxed text-lg text-white/80">
              At ClearMakes, we are committed to your satisfaction. We offer a <strong>14-day refund guarantee</strong> on every payment for a <a href="/pricing" className="text-primary hover:underline">paid plan</a>: your first payment, every renewal and yearly plans, in every ClearMakes product.
            </p>
            <p className="leading-relaxed text-lg text-white/80">
              If a <a href="/pricing" className="text-primary hover:underline">paid plan</a> isn&apos;t right for you for any reason, you can request a full refund within 14 calendar days of that payment.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">How to Request a Refund</h2>
            <p className="mb-4 leading-relaxed text-lg text-muted-foreground">
              To request a refund, please contact our billing support team at{" "}
              <EmailLink email="billing@clearmakes.com" className="text-primary hover:underline font-medium">
                billing@clearmakes.com
              </EmailLink>{" "}
              with the subject line "Refund Request". Please ensure you are emailing from the address associated with your ClearMakes account to expedite the process.
            </p>
            <p className="leading-relaxed text-lg text-muted-foreground">
              Refunds are processed within 5–10 business days and will be credited back to the original payment method used during checkout.
            </p>
            <p className="mt-4 leading-relaxed text-lg text-muted-foreground">
              Our order process is conducted by our online reseller Paddle.com, the Merchant of Record for all our
              orders, so refunds are issued by Paddle. You can also ask Paddle about any charge directly at{" "}
              <a href="https://paddle.net" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">paddle.net</a>.
              This policy covers every ClearMakes product, on monthly and yearly plans: for a yearly plan, the 14 days
              count from the date of each yearly payment, including renewals.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">After a Refund</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              When we refund a payment, we also cancel that plan, and you move to Free. Nothing is deleted: everything you
              made is kept, and the Free plan&apos;s limits apply to anything new. Upgrade any time to have everything again.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">After 14 Days</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              After the 14-day guarantee period, we do not offer refunds for subscription payments. You can, however, cancel your subscription at any time directly from Settings in the app. Your subscription will remain active until the end of the current billing cycle, and you will not be charged again. Then you move to Free, and nothing is deleted. If a payment fails, your plan keeps working while Paddle tries your card again.
            </p>
          </section>
        </div>

        {/* FAQ SCHEMA SECTION (Strict Requirement) */}
        <section className="mt-20 max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] rounded-2xl overflow-hidden transition-colors duration-300">
                <button
                  className="w-full px-8 py-6 text-left flex justify-between items-center focus:outline-none"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                >
                  <span className="font-semibold text-white pr-8 text-lg">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-primary flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence initial={false}>
                  {openFaqIndex === index && (
                    <motion.section
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 pb-8 text-muted-foreground leading-relaxed border-t border-white/5 pt-6">
                        {faq.answer}
                      </div>
                    </motion.section>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </section>

        {/* Back to Home */}
        <div className="pt-16 text-center">
          <a href="/" className="text-primary hover:text-white transition-colors font-medium flex items-center justify-center gap-2">
             <ChevronLeft size={16} /> Back to Home
          </a>
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default Refund;